/**
 * Story writing formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_writing_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const storyWritingFormatsPack: ToilePack = {
    slug: 'story-writing-formats',
    category: 'classroom',
    name: 'Story writing formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'map', label: 'Story map', subject: 'a page with boxes for "Title", "Characters", "Setting", "Problem", "Events", "Solution", "Moral"'},
        {key: 'strip', label: 'Beginning to end', subject: 'a three-panel strip labelled "Beginning", "Middle", "End" with lines under each'},
        {key: 'moral', label: 'Moral story', subject: 'a story format with a title banner and a box at the foot labelled "Moral"'},
    ],
    styles: [
        {key: 'notebook', label: 'Notebook page', style: 'school exercise notebook page, neat hand-lettered pen labels on ruled lines, red margin line', palette: 'blue ruled lines, black pen labels, red margin line', framing: 'flat top-down page filling the frame, straight on'},
        {key: 'colorcoded', label: 'Colour-coded', style: 'clean printable teaching worksheet, bold rounded headings in boxes, each part of the format in its own colour', palette: 'navy, teal and orange labels and boxes on white paper', framing: 'flat page centered with an even white margin'},
        {key: 'mono', label: 'Black and white', style: 'photocopy-ready black-and-white worksheet, thin crisp outlines, bracket and arrow callouts naming each part', palette: 'black ink only', framing: 'flat page centered with an even white margin'},
    ],
    directions: ['red pen marks', 'filled sample', 'bigger labels'],
    directionHint: 'try: red pen marks',
    seo: {
        title: 'Story Writing Format: Story Map, Beginning to End, Moral',
        metaDescription: 'Story writing format on one labelled page: a story map, a beginning-middle-end strip and a moral story page, in three styles.',
        h1: 'Story writing format: story map, beginning-middle-end, moral story',
        tagline: 'Nine labelled pages, three story plans in notebook, colour-coded and black and white styles.',
        intro: 'A printable worksheet showing how a story is planned, with each part labelled. The story map has boxes for the title, characters, setting, problem, events, solution and moral. The strip has three panels for the beginning, middle and end. The moral story page has a title banner and a box for the moral at the foot. Ask for another story plan and the same labelled layout is asked for.',
        useCases: [
            'a classroom story-writing lesson',
            'a planning sheet before a story',
            'a reading corner display',
            'a revision page for a language exam',
        ],
        faq: [
            {q: 'What does the story map include?', a: 'Boxes for the title, characters, setting, problem, events, solution and moral.'},
            {q: 'Does the page include a story?', a: 'No. The panels and boxes are labelled and the lines are ruled, so the page shows the plan, not the story.'},
            {q: 'How do the three styles differ?', a: 'Notebook page is hand-lettered pen labels on ruled lines with a red margin, like a school exercise book. Colour-coded gives each part its own colour in a box. Black and white is thin outlines in ink only, ready to photocopy.'},
            {q: 'Can I ask for a different story plan?', a: 'Yes. Add a direction for another plan and the labelled layout is asked for with it.'},
        ],
    },
}
